import { logger as newLogger } from '@util/observability';

export type Level = 'critical' | 'error' | 'warn' | 'info' | 'debug' | 'log';

/**
 * This function converts the older log format accepting any array of parameters into the new typesafe log format.
 *
 * @param values
 */
const convertLogData = (
    values: any[],
): { message: string; error: Error | undefined; context: Record<string, unknown> } => {
    // Existing log uses accept any parameter types, we need to convert to specific types

    // If any of the parameters are an error, treat the first one as the error
    const errorInstanceIndex = values.findIndex((value) => value instanceof Error);
    const errorInstance = errorInstanceIndex !== -1 ? values[errorInstanceIndex] : undefined;

    // If any of the parameters are a string, treat the first one as the message
    const messageIndex = values.findIndex((value) => typeof value === 'string');
    // If no message is found, use the error message if available, otherwise fallback to a default message
    const message =
        messageIndex !== -1 ? values[messageIndex] : errorInstance ? errorInstance.message : 'An error occurred';

    // Filter out the message and error from the other values
    const contextValues = values.filter((_, index) => index !== messageIndex && index !== errorInstanceIndex);

    // Combine the remaining params to a context object
    const context =
        contextValues.length > 0
            ? contextValues.reduce((result, value, index) => {
                  return {
                      ...result,
                      ...(typeof value === 'object' ? value : { [index]: value }),
                  };
              }, {})
            : undefined;

    return { message, error: errorInstance, context };
};

const newLog = (level: 'critical' | 'error' | 'warn' | 'info' | 'debug', values: any[]) => {
    try {
        const { message, error, context } = convertLogData(values);
        newLogger.log(level, message, error || context ? { error, context } : undefined);
    } catch {
        // If something goes wrong with the new logger, log to the console as previously
        // eslint-disable-next-line no-console
        console[level === 'critical' ? 'error' : level](...values);
    }
};

const log =
    <L extends Level>(level: L) =>
    (...values: any[]) => {
        switch (level) {
            case 'critical':
            case 'error':
            case 'warn':
            case 'info':
            case 'debug': {
                newLog(level, values);
                break;
            }
            case 'log':
            default: {
                // eslint-disable-next-line no-console
                console.log(...values);
            }
        }
    };

/**
 * @deprecated For new uses, please use the new logger from @util/observability
 */
export const logger: {
    [level in Level]: ReturnType<typeof log>;
} = {
    critical: log('critical'),
    error: log('error'),
    warn: log('warn'),
    info: log('info'),
    debug: log('debug'),
    log: log('log'),
};
